'use client'

// Buildings — the real town, exported from Blender as buildings.glb with
// every building's position + rotation BAKED, so it drops in at identity
// and lands exactly where it was placed. Draco-compressed geometry + WebP
// textures (optimised 32MB → ~1.4MB via gltf-transform: 2048 texture cap,
// dropped dual-format fallbacks, re-Draco'd).
//
// Meshes here: shop_activity_{museum,tattoo,salon,laundry}, shop_food_
// {chinese,liquor}, shop_closed_{dentist,misc_1..5}, house_{w1..w3,n1,n2},
// and since 7 Oct the street running south from the pizza place: shop_food_
// japanese, shop_mags, community_workshop and buildings_15/25 facing east,
// shop_community_art, shop_bakery_early and shop_tech facing west. Their
// boxes are obs_<name> in obstacles.glb; their stand spots are in
// sceneConfig. The whole Buildings collection is exported together and
// optimised with --ktx2 --matte; the materials that ship rough 0 from
// Blender (House01, the new Shops_32..39) are set to 1 between the two.
// (Repeats like house_w1/w2/w3/n2 share geometry — a candidate for GPU
// instancing later; placing first, optimising function later.)

import { useEffect, useMemo } from 'react'
import * as THREE from 'three/webgpu'
import { useThree } from '@react-three/fiber'
import { useModel, useKTX2Textures, preloadKTX2 } from '../hooks/useModel'
import { applyWindowLights, WINDOW_MASKS } from '../../../lib/world/windowLights'

const MODEL = '/models/buildings.glb'

export function Buildings() {
  // useModel wires the vendored Draco + KTX2 loaders (buildings.glb now has
  // KTX2 textures, so it needs the KTX2Loader).
  // The window masks load with the model, behind the veil (see windowLights):
  // started before the model suspends, so the two come in side by side.
  const gl = useThree((s) => s.gl)
  preloadKTX2(gl, WINDOW_MASKS)
  const { scene } = useModel(MODEL)
  const maskList = useKTX2Textures(WINDOW_MASKS)
  // keyed on the textures themselves, which are the same objects every
  // render; the array round them is not
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const masks = useMemo(() => new Map(WINDOW_MASKS.map((u, i) => [u, maskList[i]])), maskList)

  useEffect(() => {
    // Convert each glTF material to a fresh MeshStandardNodeMaterial so the
    // global RendererDither (which only patches NodeMaterials) reaches the
    // buildings — same proven pattern as the other textured models
    // (useNodeMaterials). We set the maps/params EXPLICITLY rather than
    // copy(): copying from a plain material drops `undefined` into the node
    // slots (colorNode, etc.) and corrupts the shader (textures vanish).
    // Shared materials convert once (dedup) to preserve instancing.
    const converted = new Map()
    scene.traverse((o) => {
      if (!o.isMesh) return
      o.castShadow = true
      o.receiveShadow = true
      const src = o.material
      if (!src || Array.isArray(src) || src.isNodeMaterial) return
      let nm = converted.get(src)
      if (!nm) {
        nm = new THREE.MeshStandardNodeMaterial()
        if (src.map) nm.map = src.map
        if (src.normalMap) nm.normalMap = src.normalMap
        if (src.aoMap) nm.aoMap = src.aoMap
        if (src.emissiveMap) nm.emissiveMap = src.emissiveMap
        nm.color = src.color?.clone() ?? new THREE.Color('white')
        if (src.emissive) nm.emissive = src.emissive.clone()
        nm.roughness = src.roughness ?? 1
        nm.metalness = src.metalness ?? 0
        nm.side = src.side
        nm.transparent = src.transparent
        nm.opacity = src.opacity
        nm.alphaTest = src.alphaTest
        nm.vertexColors = src.vertexColors
        nm.flatShading = src.flatShading
        // Windows, for the buildings that have a mask. Keyed by the glTF
        // material name, which survives the conversion above only because
        // we read it off `src` here, before nm replaces it on the mesh.
        applyWindowLights(nm, src.name, masks)
        converted.set(src, nm)
      }
      o.material = nm
    })
  }, [scene, masks])

  // Transforms are baked into the GLB — render at identity.
  return <primitive object={scene} />
}

export default Buildings
